/**
 * Settings → Connections → Socials: connect Instagram, LinkedIn, X, Facebook and
 * YouTube through Auteur's managed Postiz.
 *
 * "Connect" asks the server for a one-time link for this organization and opens
 * it in a new tab; the customer authorizes on the platform's own screen. "I've
 * finished" asks the server to bind the account that just appeared. The server
 * binds by id to this organization only and never guesses: if two connections
 * happen at once it says support will finish it.
 */
import { useCallback, useEffect, useState } from 'react';

import {
  claimSocialConnect,
  disconnectSocial,
  fetchSocialConnections,
  startSocialConnect,
  type SocialConnections,
} from '../../api/social';

const STATUS_LABEL: Record<string, string> = {
  available: 'Works now',
  review_pending: 'Platform review pending',
  not_available: 'Not available yet',
};

const CLAIM_MESSAGE: Record<string, string> = {
  pending: 'We do not see the new account yet. Finish the sign-in in the other tab, then try again.',
  expired: 'That link expired. Start again with Connect.',
  needs_operator: 'Another connection happened at the same moment, so we did not guess. Auteur support will finish it for you.',
  bound: 'Connected.',
};

export function SocialConnectView({ state, pending, message, onConnect, onFinish, onDisconnect }: {
  state: SocialConnections | null;
  pending: { platform: string; inviteId: string } | null;
  message: string;
  onConnect: (platform: string) => void;
  onFinish: () => void;
  onDisconnect: (channelId: string) => void;
}) {
  if (!state) return <p className="social-connect-loading">Loading your social accounts…</p>;
  return (
    <div className="social-connect" data-section="social-connect">
      <p>
        Posts publish only to the accounts you connect here, and only after you approve the post in your
        workspace (Social → Post now or Schedule).
      </p>
      {!state.relayReachable && <p className="social-connect-warn">Social posting is temporarily unreachable; your connections are kept.</p>}
      <h4>Your connected accounts</h4>
      {state.channels.length === 0 ? (
        <p>None yet.</p>
      ) : (
        <ul className="social-connect-channels">
          {state.channels.map((channel) => (
            <li key={channel.channel_id}>
              <strong>{channel.display_name || channel.platform}</strong> ({channel.platform})
              {channel.connected ? '' : ' — needs reconnecting'}{' '}
              <button type="button" className="secondary" onClick={() => onDisconnect(channel.channel_id)}>Disconnect</button>
            </li>
          ))}
        </ul>
      )}
      <h4>Connect an account</h4>
      <ul className="social-connect-platforms">
        {state.platforms.map((platform) => (
          <li key={platform.id} data-platform={platform.id}>
            {platform.connectable ? (
              <button type="button" onClick={() => onConnect(platform.id)} disabled={!!pending}>
                Connect {platform.label}
              </button>
            ) : (
              <strong>{platform.label}</strong>
            )}{' '}
            <span className={`social-connect-status social-connect-status-${platform.status}`}>
              {STATUS_LABEL[platform.status] || platform.status}
            </span>
            <span className="social-connect-note"> {platform.note}</span>
          </li>
        ))}
      </ul>
      {pending && (
        <p className="social-connect-pending">
          Finish signing in to {pending.platform} in the new tab, then{' '}
          <button type="button" onClick={onFinish}>I've finished connecting</button>
        </p>
      )}
      {message && <p className="social-connect-message" role="status">{message}</p>}
    </div>
  );
}

export function SocialConnectPanel() {
  const [state, setState] = useState<SocialConnections | null>(null);
  const [pending, setPending] = useState<{ platform: string; inviteId: string } | null>(null);
  const [message, setMessage] = useState('');

  const reload = useCallback(async () => {
    try {
      setState(await fetchSocialConnections());
    } catch {
      setMessage('Social connections are not available right now.');
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const onConnect = useCallback(async (platform: string) => {
    setMessage('');
    try {
      const invite = await startSocialConnect(platform);
      setPending({ platform, inviteId: invite.inviteId });
      window.open(invite.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not start the connection.');
    }
  }, []);

  const onFinish = useCallback(async () => {
    if (!pending) return;
    try {
      const result = await claimSocialConnect(pending.inviteId);
      setMessage(CLAIM_MESSAGE[result.state] || CLAIM_MESSAGE.pending);
      if (result.state !== 'pending') setPending(null);
      if (result.state === 'bound') await reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not finish the connection.');
    }
  }, [pending, reload]);

  const onDisconnect = useCallback(async (channelId: string) => {
    try {
      await disconnectSocial(channelId);
      await reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not disconnect.');
    }
  }, [reload]);

  return (
    <SocialConnectView state={state} pending={pending} message={message}
      onConnect={(p) => void onConnect(p)} onFinish={() => void onFinish()} onDisconnect={(c) => void onDisconnect(c)} />
  );
}
